// src/services/vibeShare.js — share a Vibes post into a chat, the app's way.
//
// The app's share sends a `shared_post` message: a PLAINTEXT card (postId, caption,
// thumbnail, owner) — post cards are public content, so nothing to seal except the
// notification preview text, exactly like a media forward. The receiving app already
// renders these bubbles (MessageBubble's shared_post branch) and taps through to the
// post — so a web share lands on phones fully functional, and vice versa.
import {
  doc, collection, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
// ⭐ generated twin of allo-app/services/vibeChatCard.ts — do not edit the copy
import { vibeChatCard } from './vibeChatCard';
import { sealForUsers } from '../crypto/e2ee';
import { relayNewMessage } from './socket';
import { writeChatDoc } from './mediaUpload';
import { sealMembers } from './groupMembers';
import { setMessageDoc } from './sendDoc'; // 🖥📱 origin + my other devices' copy

const PREVIEW = '🔗 Shared Post';

export async function shareVibePost({ post, chat, myUid, myName }) {
  if (!post?.id || !chat?.id) throw new Error('Nothing to share.');
  const participants = sealMembers(chat);   // 🔐 active members only
  const isGroup = !!chat.isGroup;
  const clientTempId = `temp-share-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // seal the notification preview (the card itself is plaintext, like the app)
  let textFields = { text: PREVIEW };
  try {
    const sealed = await sealForUsers(PREVIEW, participants);
    if (sealed && Object.keys(sealed.perDeviceKeys || {}).length) {
      textFields = {
        text: '\u{1F512} [Encrypted Message]',
        perDeviceKeys: sealed.perDeviceKeys,
        senderDeviceId: sealed.senderDeviceId,
      };
    }
  } catch {}

  /* 🟥 THIS WAS THE FOURTH HAND-WRITTEN COPY of the card the phone writes three
     times, and it already disagreed with them: it fell back to 'Allo user' where
     the phone said 'Unknown', and counted media without the videoUrl case. Both
     differences were invisible until you looked at one share on two devices.
     🟢 vibeChatCard is generated from the phone's own services/vibeChatCard.ts,
        so there is one definition of what a shared post IS. */
  const card = {
    ...vibeChatCard(post),
    sharedBy: myUid,
    sharedByName: myName || 'Web',
  };

  const msgRef = doc(collection(db, 'chats', chat.id, 'messages'));
  const messageId = msgRef.id;
  await setMessageDoc(msgRef, {
    type: 'shared_post',
    clientTempId,
    senderId: myUid,
    timestamp: serverTimestamp(),
    sent: true,
    delivered: false,
    seen: false,
    ...textFields,
    ...card,
    ...(isGroup ? { senderName: myName || 'Web', isGroup: true } : {}),
  });

  relayNewMessage({
    chatId: chat.id, messageId, fromUserId: myUid, participants, isGroup,
    cloudText: PREVIEW, clientMsgId: clientTempId,
    extra: {
      type: 'shared_post',
      ...card,
      ...(textFields.perDeviceKeys ? {
        perDeviceKeys: textFields.perDeviceKeys,
        senderDeviceId: textFields.senderDeviceId,
      } : {}),
      ...(isGroup ? { senderName: myName || 'Web' } : {}),
    },
  });

  await writeChatDoc({ chat, myUid, myName, messageId, preview: PREVIEW });
  return messageId;
}
